import React, { FC, Key, useEffect, useMemo, useState } from 'react';
import { Button, Tooltip, Tree } from 'antd';
import { AntTreeNodeProps } from 'antd/es/tree';
import { useNavigate, useParams } from 'react-router-dom';
import { WarningCard } from 'common/components/WarningCard';
import { ERoute } from 'common/const/route.enum';
import { EOrderDirection, EOrderField } from 'common/const/common.enum';
import { showMessage, showSuccessMessage } from 'common/helpers/message.helper';
import { getItemKeyFromTreeKey } from 'common/helpers/tree.helper';
import { useSubdivisionContext } from 'common/context';
import { LIST_LIMIT_0 } from 'common/config';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { ReactComponent as ArrowRightShortIcon } from 'app/assets/images/redesign/arrow-right-short.svg';
import { ReactComponent as ArrowDownShortIcon } from 'app/assets/images/redesign/arrow-down-short.svg';
import { ReactComponent as PlusIcon } from 'app/assets/images/redesign/plus.svg';
import { ReactComponent as BinIcon } from 'app/assets/images/redesign/bin.svg';
import { SubdivisionTreeModal } from 'entities/Subdivision/components';
import { ESettingsMenuTab, ESettingsTab } from 'entities/Settings/Settings.enum';
import { getSubdivisionTreeKeysBySubdivisionId, mapSubdivisionListToTreeData } from 'entities/Settings/Settings.helper';
import { SubdivisionCreateCard } from 'entities/Subdivision/components/SubdivisionCreateCard';
import { ISubdivision } from 'entities/Subdivision/Subdivision.models';
import { findSubdivisionChildrenIds } from 'entities/Subdivision/Subdivision.helper';
import { getLegalList } from 'entities/LegalEntity/Legal.slice';
import {
  deleteSubdivision,
  filterSubdivisionList,
  selectIsSubdivisionLoading,
  selectSubdivisionList,
  updateSubdivision,
} from 'entities/Subdivision/Subdivision.slice';
import { getUserList, selectCurrentUser } from 'entities/User/User.slice';

interface IComponentProps {
  active: boolean;
}

export const SettingsSidebarSubdivisionTree: FC<IComponentProps> = ({ active }) => {
  const dispatch = useAppDispatch();
  // subdivision
  const subdivisionList = useAppSelector(selectSubdivisionList);
  const isSubdivisionLoading = useAppSelector(selectIsSubdivisionLoading);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [subdivisionTreeKey, setSubdivisionTreeKey] = useState<Key>('');
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [expandedTreeKeys, setExpandedTreeKeys] = useState<Key[]>([]);
  const [openWarningCard, setOpenWarningCard] = useState<boolean>(false);
  const [showSubdivisionTree, setShowSubdivisionTree] = useState<boolean>(false);
  const [openSubdivisionTransferModal, setOpenSubdivisionTransferModal] = useState<boolean>(false);
  const { id, itemMenu } = useParams();
  const navigate = useNavigate();
  const { setParentId, setOpenSubdivisionCreateCard } = useSubdivisionContext();
  const filteredSubdivisions = useMemo(() => {
    const key = getItemKeyFromTreeKey(subdivisionTreeKey);

    return subdivisionList.filter((item) => item.id !== Number(key));
  }, [subdivisionList, subdivisionTreeKey]);

  const accountId = currentUser?.account?.id;
  const selectedSubdivisionTreeKeys = useMemo(
    () => getSubdivisionTreeKeysBySubdivisionId(subdivisionList, id),
    [subdivisionList, id],
  );

  const onCreateSubdivisionClick = async (key?: Key) => {
    const parentId = key ? Number(getItemKeyFromTreeKey(key)) : undefined;

    await dispatch(
      getUserList({
        accountId,
        orderField: EOrderField.LastName,
        orderDirection: EOrderDirection.ASC,
        limit: LIST_LIMIT_0,
      }),
    );
    await dispatch(getLegalList({ limit: LIST_LIMIT_0 }));
    setParentId(parentId);
    setOpenSubdivisionCreateCard(true);
  };

  const onTransferSubdivisionClick = (key: Key) => {
    setSubdivisionTreeKey(key);
    setOpenSubdivisionTransferModal(true);
  };

  const onTransferSubdivisionSaveClick = (parentId: number, onClose: () => void) => {
    const subdivisionKey = getItemKeyFromTreeKey(subdivisionTreeKey);

    dispatch(
      updateSubdivision({
        id: Number(subdivisionKey),
        parentId,
        onSuccess: () => {
          showSuccessMessage('Подразделение успешно перенесено.');
          onClose();
        },
      }),
    );
  };

  const onDeleteSubdivisionClick = (key: Key) => {
    const subdivisionKey = getItemKeyFromTreeKey(key);

    setSubdivisionTreeKey(subdivisionKey);
    setOpenWarningCard(true);
  };

  const onDeleteSubdivisionConfirmClick = () => {
    const subdivisionId = Number(subdivisionTreeKey as string);

    dispatch(
      deleteSubdivision({
        id: subdivisionId,
        onSuccess: () => {
          const subdivision = subdivisionList.find((item) => item.id === subdivisionId) as ISubdivision;
          const subdivisionAndChildrenIds = findSubdivisionChildrenIds(subdivisionId, subdivisionList);

          setOpenWarningCard(false);
          showMessage({
            content: `Подразделение ${subdivision.name} удалено.`,
            icon: <BinIcon className="icon-bin-dark-grey" />,
          });
          dispatch(filterSubdivisionList(subdivisionAndChildrenIds));

          if (id && subdivisionAndChildrenIds.includes(Number(id))) {
            if (subdivision.parentId) {
              navigate(`${ERoute.Settings}/${ESettingsTab.Subdivision}/${subdivision.parentId}/${ESettingsMenuTab.General}`);
            } else {
              navigate(`${ERoute.Settings}/${ESettingsTab.Account}/${ESettingsMenuTab.Contracts}`);
            }
          }
        },
      }),
    );
  };

  const onExpandTreeKeys = (keys: Key[]) => {
    setExpandedTreeKeys(keys);
    setAutoExpandParent(false);
  };

  const subdivisionTreeData = useMemo(() => {
    return mapSubdivisionListToTreeData(
      subdivisionList,
      onCreateSubdivisionClick,
      onTransferSubdivisionClick,
      onDeleteSubdivisionClick,
    );
  }, [subdivisionList, id]);

  useEffect(() => {
    if (selectedSubdivisionTreeKeys.length) {
      setShowSubdivisionTree(true);
      setExpandedTreeKeys((prev) => [...new Set([...prev, ...selectedSubdivisionTreeKeys])]);
      setAutoExpandParent(true);
    }
  }, [selectedSubdivisionTreeKeys]);

  return (
    <>
      <div className={`settings-sidebar__btn hover ${active ? 'active' : ''}`}>
        <div className="settings-sidebar__btn-container">
          <Button
            className="button-icon settings-sidebar__btn-toggle"
            icon={
              showSubdivisionTree ? (
                <ArrowDownShortIcon className="icon-arrow-down-short-dark-grey" />
              ) : (
                <ArrowRightShortIcon className="icon-arrow-right-short-dark-grey" />
              )
            }
            onClick={() => setShowSubdivisionTree((prev) => !prev)}
          />

          <div className="settings-sidebar__btn-title-container">
            <span className="text-h4-item-name settings-sidebar__btn-text">Подразделения</span>

            <Tooltip title="Новое подразделение" placement="bottom">
              <Button
                className="button-icon settings-sidebar__btn-create"
                icon={<PlusIcon className="icon-plus-dark-grey" />}
                onClick={() => onCreateSubdivisionClick()}
              />
            </Tooltip>
          </div>
        </div>

        <span className="text-tag color-dark-grey settings-sidebar__btn-text">Настройки и создание подразделений</span>
      </div>

      {showSubdivisionTree && (
        <Tree
          className="settings-sidebar__subdivision-tree mb-14"
          treeData={subdivisionTreeData}
          showIcon
          switcherIcon={(props: AntTreeNodeProps) => {
            return props.expanded ? (
              <ArrowDownShortIcon className="icon-arrow-down-short-dark-grey" />
            ) : (
              <ArrowRightShortIcon className="icon-arrow-right-short-dark-grey" />
            );
          }}
          selectedKeys={selectedSubdivisionTreeKeys}
          expandedKeys={expandedTreeKeys}
          autoExpandParent={autoExpandParent}
          onExpand={onExpandTreeKeys}
          onSelect={(keys) => {
            if (!keys.length) {
              return;
            }

            const subdivisionKey = getItemKeyFromTreeKey(keys[0]);

            navigate(
              `${ERoute.Settings}/${ESettingsTab.Subdivision}/${subdivisionKey}/${
                // tab !== ESettingsTab.Subdivision ? ESettingsMenuTab.General : menu
                !itemMenu ? ESettingsMenuTab.General : itemMenu
              }`,
            );
          }}
        />
      )}

      <SubdivisionCreateCard />

      <SubdivisionTreeModal
        open={openSubdivisionTransferModal}
        loading={isSubdivisionLoading}
        placeholder="Перенести в..."
        subdivisions={filteredSubdivisions}
        onCancel={() => setOpenSubdivisionTransferModal(false)}
        onSubmit={onTransferSubdivisionSaveClick}
      />

      <WarningCard
        open={openWarningCard}
        content="Вы уверены, что хотите удалить подразделение?"
        subtitle="Отменить данное действие будет невозможно."
        confirmBtnTitle="Удалить подразделение"
        loading={isSubdivisionLoading}
        onConfirm={onDeleteSubdivisionConfirmClick}
        onClose={() => setOpenWarningCard(false)}
      />
    </>
  );
};
